import { endpoints } from '@/services/endpoints'
import { createSharedSseConnection } from '@/services/sse'

/**
 * The account-wide work-order stream, as one shared connection.
 *
 * Same arrangement as `features/anomalies/liveStream.js`, and for the same
 * reasons — read that header for the long version. In short:
 *
 *   ONE socket, ref-counted, with a five-second linger, because a browser gives
 *   an HTTP/1.1 origin six connections and a spare one is not free.
 *
 *   The event is a TRIGGER to refetch, never a row to apply. A changed row can
 *   enter or leave a filter or move pages, none of which is computable here —
 *   and `last_payload()` pops server-side, so with two tabs open on one account
 *   one of them is handed nothing at all. An empty event still means "refetch".
 *
 *   Coalesced: trailing 400ms, forced at 2s, held while the tab is hidden.
 *
 * ── A DELETE is never announced ──────────────────────────────────────────
 * The soft delete fires the trigger, but the server's stream drops any row it
 * can no longer find, so no event reaches anyone. An order deleted elsewhere
 * stays on an open list until something else refetches it. The delete made
 * HERE invalidates the list itself, which covers the person who did it.
 * Same gap as the handover stream, recorded in BACKEND-GAPS.md §6a.
 */
export const LIST_COALESCE_MS = 400
export const LIST_COALESCE_MAX_MS = 2000

const listeners = new Set()
let statusValue = 'idle'
const statusWatchers = new Set()
let flushTimer = null
let maxWaitTimer = null
let pendingWhileHidden = false

const flush = () => {
  clearTimeout(flushTimer)
  clearTimeout(maxWaitTimer)
  flushTimer = null
  maxWaitTimer = null
  pendingWhileHidden = false
  for (const listener of listeners) listener()
}

const schedule = () => {
  if (typeof document !== 'undefined' && document.hidden) {
    pendingWhileHidden = true
    return
  }
  clearTimeout(flushTimer)
  flushTimer = setTimeout(flush, LIST_COALESCE_MS)
  maxWaitTimer ??= setTimeout(flush, LIST_COALESCE_MAX_MS)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && pendingWhileHidden) flush()
  })
}

const connection = createSharedSseConnection({
  path: endpoints.workOrders.stream,
  lingerMs: 5000,
  onEvent: schedule,
  /* No `Last-Event-ID` replay, so a reconnect is a gap and a gap is news. */
  onOpen: ({ reconnected }) => {
    if (reconnected) schedule()
  },
  onStatus: (status) => {
    statusValue = status
    for (const watcher of statusWatchers) watcher(status)
  },
})

/**
 * Subscribe to "the board may have changed". Returns an unsubscribe.
 * The socket opens on the first subscriber and closes a linger after the last.
 */
export function subscribeToWorkOrderList(onChange, onStatusChange) {
  listeners.add(onChange)
  if (onStatusChange) {
    statusWatchers.add(onStatusChange)
    onStatusChange(statusValue)
  }
  const release = connection.subscribe()

  return () => {
    listeners.delete(onChange)
    if (onStatusChange) statusWatchers.delete(onStatusChange)
    release()
    if (listeners.size === 0) {
      clearTimeout(flushTimer)
      clearTimeout(maxWaitTimer)
      flushTimer = null
      maxWaitTimer = null
      pendingWhileHidden = false
    }
  }
}
